import { useMemo } from 'react'
import PropTypes from 'prop-types'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { useTranslation } from 'react-i18next'
import { getBarChartOptions, getColumnChartOptions, getDonutChartOptions } from 'config/highcharts'
import { REASONS, REASON_COLORS, daysBetween } from './format'

const HOURS = Array.from({ length: 24 }, (_, hour) => hour)
const TOP_PREFIXES = 15

// One series per reason over `categories`, from rows of { [key], reason, total }.
const seriesByReason = (rows, categories, key, t) => REASONS.map(reason => ({
  name: t(`system.scanRejections.reasons.${reason}`),
  color: REASON_COLORS[reason],
  data: categories.map(category => {
    const row = rows.find(r => String(r[key]) === String(category) && r.reason === reason)
    return row ? row.total : 0
  })
})).filter(series => series.data.some(Boolean))

const ChartCard = ({ children }) => (
  <div className='rounded-lg border border-gray-200 bg-white p-4'>{children}</div>
)

ChartCard.propTypes = {
  children: PropTypes.node
}

const Charts = ({ summary, onPrefixClick }) => {
  const { t } = useTranslation()
  const { byDay = [], byHour = [], byPrefix = [], byMatchedBy = [], dateFrom, dateTo } = summary || {}

  const dayOptions = useMemo(() => {
    const days = daysBetween(dateFrom, dateTo)
    return getColumnChartOptions({
      title: t('system.scanRejections.charts.byDay'),
      categories: days.map(day => day.slice(5)),
      series: seriesByReason(byDay, days, 'day', t),
      stacked: true
    })
  }, [byDay, dateFrom, dateTo, t])

  const hourOptions = useMemo(() => getColumnChartOptions({
    title: t('system.scanRejections.charts.byHour'),
    categories: HOURS.map(hour => `${String(hour).padStart(2, '0')}:00`),
    series: seriesByReason(byHour, HOURS, 'hour', t),
    stacked: true
  }), [byHour, t])

  const prefixOptions = useMemo(() => {
    const top = byPrefix.slice(0, TOP_PREFIXES)
    const label = row => row.prefix || t('system.scanRejections.noPrefix')
    return getBarChartOptions({
      title: t('system.scanRejections.charts.byPrefix'),
      categories: top.map(label),
      height: Math.max(320, top.length * 32),
      series: [{ name: t('system.scanRejections.cols.rejections'), data: top.map(row => row.rejections) }],
      // By index: with every tenant shown, two farms can share a prefix.
      onPointClick: (category, index) => {
        if (top[index]) onPrefixClick(top[index])
      },
      tooltipFormatter: function () {
        const row = top[this.point.index]
        return `<b>${this.x}</b> (${row.farmName})<br/>` +
          `${t('system.scanRejections.cols.rejections')}: <b>${row.rejections}</b><br/>` +
          `${t('system.scanRejections.cols.accepted')}: <b>${row.accepted}</b><br/>` +
          `${t('system.scanRejections.cols.rejectionRate')}: <b>${row.rejectionRate ?? 0}%</b>`
      }
    })
  }, [byPrefix, onPrefixClick, t])

  const matchedByOptions = useMemo(() => {
    const data = byMatchedBy.map(row => ({ name: row.matchedBy, y: row.total }))
    const total = data.reduce((sum, item) => sum + item.y, 0)
    const options = getDonutChartOptions({
      data,
      title: t('system.scanRejections.charts.matchedByTotal', { total })
    })
    return { ...options, chart: { ...options.chart, height: 320 } }
  }, [byMatchedBy, t])

  return (
    <div className='grid grid-cols-1 xl:grid-cols-2 gap-4'>
      <ChartCard>
        <HighchartsReact highcharts={Highcharts} options={dayOptions} />
      </ChartCard>
      <ChartCard>
        <HighchartsReact highcharts={Highcharts} options={hourOptions} />
      </ChartCard>
      <ChartCard>
        {byPrefix.length
          ? <HighchartsReact highcharts={Highcharts} options={prefixOptions} />
          : <p className='text-sm text-gray-500'>{t('system.scanRejections.empty')}</p>}
      </ChartCard>
      <ChartCard>
        <h4 className='text-sm font-semibold mb-2'>{t('system.scanRejections.charts.byMatchedBy')}</h4>
        {byMatchedBy.length
          ? <HighchartsReact highcharts={Highcharts} options={matchedByOptions} />
          : <p className='text-sm text-gray-500'>{t('system.scanRejections.empty')}</p>}
      </ChartCard>
    </div>
  )
}

Charts.propTypes = {
  summary: PropTypes.object,
  onPrefixClick: PropTypes.func.isRequired
}

export default Charts
